import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EXHIBITION_SLUG = 'this-is-humanity';

// How many ambient memory thumbnails surface at once. Keeps the DOM light +
// the entity central — this is a waiting ritual, not a gallery.
const MAX_VISIBLE = 6;
// Avoid spawning fragments that would overlap the central entity / loading
// copy. Coordinates are normalised (0..1); the centre is (0.5, 0.5).
const CENTRE_EXCLUSION_R = 0.20;
// Lifetime envelope (seconds). Long so the fragment can drift downward like
// a piece of code being read, then dissolve.
const LIFE_MIN_S = 9.0;
const LIFE_MAX_S = 13.5;
// Spawn cadence — irregular so the field never feels metronomic.
const SPAWN_MIN_MS = 950;
const SPAWN_MAX_MS = 1900;

/**
 * LoadingMemoryField — ambient memory fragments rendered behind the entity
 * during the image-forming state.
 *
 * Each fragment surfaces inside the falling code stream:
 *   1. fades in from blur(8px) + opacity 0 near the top
 *   2. drifts downward across most of the viewport over its lifetime
 *   3. carries a `screen`-blended halo that brightens the code rain around
 *      it (the canvas is below this layer, the halo glows whatever it
 *      overlays — including code glyphs) — this is the "code glitch" effect
 *   4. dissolves back to blur(10px) + opacity 0 near the bottom
 *
 * Performance notes:
 *  - at most 6 fragments alive at a time
 *  - single setTimeout-driven spawner, cleaned up on unmount
 *  - all motion handled by Framer Motion (transform + opacity only)
 *  - <img> uses loading="lazy" + decoding="async" and renders at small CSS
 *    sizes so the browser scales + caches without blocking the main thread
 *  - each surface fires a very quiet `memorySurface` cue via the shared
 *    audio module; cues respect the global mute and duck under voice
 */
export default function LoadingMemoryField({ active = true }) {
  const [items, setItems] = useState([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // visible: array of { key, item, x, yStart, yEnd, depth, ttl, driftX }
  const [visible, setVisible] = useState([]);
  const counterRef = useRef(0);
  const aliveRef = useRef(true);

  // ── one-shot gallery fetch ────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/humanity/gallery`, {
          params: { exhibition_slug: EXHIBITION_SLUG, limit: 30 },
          timeout: 10000,
        });
        if (cancelled) return;
        const arr = Array.isArray(r.data?.items) ? r.data.items : [];
        // Shuffle so each visitor sees a different sequence.
        for (let i = arr.length - 1; i > 0; i -= 1) {
          const j = Math.floor(Math.random() * (i + 1));
          [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        setItems(arr);
      } catch {
        /* non-fatal — the waiting ritual still works without thumbnails. */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── spawner loop ──────────────────────────────────────────
  useEffect(() => {
    aliveRef.current = active;
    if (!active) return undefined;

    let timer = 0;
    const schedule = () => {
      const delay = SPAWN_MIN_MS + Math.random() * (SPAWN_MAX_MS - SPAWN_MIN_MS);
      timer = setTimeout(spawn, delay);
    };

    function spawn() {
      if (!aliveRef.current) return;
      const pool = itemsRef.current;
      if (pool.length) {
        // Pick a random X outside the centre exclusion zone.
        let x = 0.5;
        for (let tries = 0; tries < 8; tries += 1) {
          x = 0.08 + Math.random() * 0.84;
          if (Math.abs(x - 0.5) > CENTRE_EXCLUSION_R) break;
        }
        // Y travels downward from above the viewport to below it; each
        // fragment lives long enough for the eye to follow its descent.
        const yStart = -0.06 - Math.random() * 0.08;
        const yEnd = 1.04 + Math.random() * 0.08;
        const item = pool[Math.floor(Math.random() * pool.length)];
        const depth = 0.5 + Math.random() * 0.65;
        const ttl = LIFE_MIN_S + Math.random() * (LIFE_MAX_S - LIFE_MIN_S);
        const driftX = (Math.random() - 0.5) * 14; // very subtle sway
        const key = ++counterRef.current;
        setVisible((vs) => {
          const next = vs.length >= MAX_VISIBLE ? vs.slice(1) : vs;
          return [...next, { key, item, x, yStart, yEnd, depth, ttl, driftX }];
        });
        setTimeout(() => {
          if (!aliveRef.current) return;
          setVisible((vs) => vs.filter((v) => v.key !== key));
        }, ttl * 1000);
      }
      schedule();
    }
    schedule();

    return () => {
      aliveRef.current = false;
      if (timer) clearTimeout(timer);
    };
  }, [active]);

  if (!items.length) return null;
  return (
    <div
      className="fixed inset-0 z-[8] pointer-events-none"
      aria-hidden
      data-testid="loading-memory-field"
    >
      <AnimatePresence>
        {visible.map((v) => (
          <MemoryFragment key={v.key} v={v} />
        ))}
      </AnimatePresence>
    </div>
  );
}

function MemoryFragment({ v }) {
  // CSS px size — scaled by depth so closer fragments read larger.
  const baseW = 120;
  const baseH = 160; // 3:4
  const w = baseW * v.depth;
  const h = baseH * v.depth;
  const peakOpacity = 0.32 * v.depth + 0.10; // ~0.25–0.40
  return (
    <motion.div
      // Top position is animated separately from x so we can drift across
      // most of the viewport while keeping translateX subtle.
      initial={{ top: `${v.yStart * 100}%`, opacity: 0, x: 0, filter: 'blur(8px)' }}
      animate={{
        top: `${v.yEnd * 100}%`,
        opacity: [0, peakOpacity, peakOpacity, 0],
        x: [0, v.driftX * 0.4, v.driftX * 0.8, v.driftX],
        filter: ['blur(8px)', 'blur(0.6px)', 'blur(0.6px)', 'blur(10px)'],
      }}
      exit={{ opacity: 0, filter: 'blur(12px)' }}
      transition={{
        duration: v.ttl,
        times: [0, 0.18, 0.78, 1],
        ease: 'easeInOut',
      }}
      style={{
        position: 'absolute',
        left: `${v.x * 100}%`,
        width: `${w}px`,
        height: `${h}px`,
        marginLeft: `${-w / 2}px`,
        marginTop: `${-h / 2}px`,
      }}
    >
      {/* Code-glitch halo — additive blend so it brightens whatever lies
          behind (the AmbientCanvas with falling glyphs). A subtle CSS
          flicker keyframe shifts the radius so nearby code appears to
          breathe / disturb. */}
      <div
        className="humanity-memory-halo absolute pointer-events-none"
        style={{
          left: '-46%',
          top: '-30%',
          width: '192%',
          height: '160%',
        }}
      />
      <div
        className="relative w-full h-full rounded-[6px] overflow-hidden"
        style={{
          // No hard UI border — soft inner shadow so the image feels
          // submerged in the code, not stuck on top.
          boxShadow:
            '0 0 22px rgba(144, 224, 239, 0.10),'
            + ' inset 0 0 18px rgba(0,0,0,0.55),'
            + ' 0 0 1px rgba(190, 232, 238, 0.30)',
        }}
      >
        <img
          src={v.item.image_url}
          alt=""
          loading="lazy"
          decoding="async"
          className="w-full h-full block"
          style={{
            objectFit: 'cover',
            filter: 'saturate(0.78) contrast(0.92) brightness(0.92)',
          }}
        />
        {/* Soft radial vignette — pulls the fragment back into shadow so
            it reads as a memory surfacing, not a thumbnail card. */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.62) 100%)',
          }}
        />
        {/* Top scanline glitch — purely cosmetic, hints at "data". */}
        <div
          className="humanity-memory-scanline pointer-events-none absolute inset-x-0"
          style={{ height: '1px' }}
        />
      </div>
    </motion.div>
  );
}
